import { esc, showToast } from '../ui.js';
import { disableForSave } from './autosave-focus.js';

const COPY = {
  en: {
    title: 'Webhooks', description: 'Send signed task events to a public HTTP(S) endpoint.',
    url: 'Endpoint URL', events: 'Events', active: 'Active', create: 'Create webhook', save: 'Save',
    test: 'Send test', remove: 'Delete', confirm: 'Confirm delete', cancel: 'Cancel', refresh: 'Refresh delivery status',
    empty: 'No webhooks yet.', secret: 'Save this signing secret now. It is shown only once.', dismiss: 'Dismiss secret',
    failed: 'Could not save or load webhooks. Try again.', queued: 'Test queued for delivery.',
    pending: 'Pending', delivering: 'Delivering', succeeded: 'Delivered', failedDelivery: 'Failed',
    none: 'No deliveries', attempts: 'Attempts', last: 'Last delivery',
    task_created: 'Task created', status_changed: 'Status changed', assigned: 'Assignee changed', comment: 'Comment added', updated: 'Task updated',
    webhook_url_invalid: 'Enter a public HTTP(S) URL without credentials or a fragment.',
    webhook_fields_invalid: 'Check the webhook fields.', webhook_events_invalid: 'Select at least one event.',
    webhook_active_invalid: 'Active must be a boolean.', webhook_inactive: 'Enable this webhook before sending a test.',
    webhook_not_found: 'Webhook no longer exists. Refresh the list.',
  },
  uk: {
    title: 'Вебхуки', description: 'Надсилайте підписані події завдань на публічну HTTP(S) адресу.',
    url: 'URL отримувача', events: 'Події', active: 'Активний', create: 'Створити вебхук', save: 'Зберегти',
    test: 'Надіслати тест', remove: 'Видалити', confirm: 'Підтвердити видалення', cancel: 'Скасувати', refresh: 'Оновити стан доставки',
    empty: 'Вебхуків поки немає.', secret: 'Збережіть секрет підпису зараз. Він показується лише один раз.', dismiss: 'Приховати секрет',
    failed: 'Не вдалося зберегти або завантажити вебхуки. Спробуйте ще раз.', queued: 'Тест додано до черги доставки.',
    pending: 'У черзі', delivering: 'Надсилається', succeeded: 'Доставлено', failedDelivery: 'Помилка',
    none: 'Доставок немає', attempts: 'Спроби', last: 'Остання доставка',
    task_created: 'Завдання створено', status_changed: 'Статус змінено', assigned: 'Виконавця змінено', comment: 'Коментар додано', updated: 'Завдання оновлено',
    webhook_url_invalid: 'Введіть публічну HTTP(S) адресу без облікових даних і фрагмента.',
    webhook_fields_invalid: 'Перевірте поля вебхука.', webhook_events_invalid: 'Виберіть принаймні одну подію.',
    webhook_active_invalid: 'Поле активності має бути логічним значенням.', webhook_inactive: 'Увімкніть вебхук перед надсиланням тесту.',
    webhook_not_found: 'Вебхук більше не існує. Оновіть список.',
  },
};
export const webhookText = (locale, key) => (COPY[locale === 'uk' ? 'uk' : 'en'][key] || COPY.en[key] || key);

export function webhookFormBody(form) {
  return { url: form.elements.url.value.trim(), active: form.elements.active.checked,
    events: [...form.querySelectorAll('input[name="events"]:checked')].map(input => input.value) };
}

export function renderWebhookForm(webhook, events, locale) {
  const t = key => webhookText(locale, key);
  return `<form data-webhook-id="${webhook?.id || ''}" class="card mb-2 webhook-form">
    <label>${t('url')}<input class="form-input" name="url" type="url" required maxlength="2048" value="${esc(webhook?.url || '')}" placeholder="https://example.com/webhook"></label>
    <fieldset class="webhook-events"><legend>${t('events')}</legend>${events.map(event => `<label>
      <input type="checkbox" name="events" value="${esc(event)}" ${webhook?.events.includes(event) ? 'checked' : ''}> ${esc(t(event))}</label>`).join('')}</fieldset>
    <label><input type="checkbox" name="active" ${webhook?.active !== false ? 'checked' : ''}> ${t('active')}</label>
    ${webhook ? `<p>${t('last')}: ${esc(webhook.delivery_status ? t(webhook.delivery_status === 'failed' ? 'failedDelivery' : webhook.delivery_status) : t('none'))}
      ${webhook.delivery_id ? ` · HTTP ${webhook.delivery_status_code ?? '—'} · ${t('attempts')}: ${Number(webhook.delivery_attempts)}` : ''}</p>` : ''}
    <div class="webhook-actions"><button type="submit" class="btn btn-primary">${t(webhook ? 'save' : 'create')}</button>
    ${webhook ? `<button type="button" class="btn" data-action="test">${t('test')}</button>
      <button type="button" class="btn btn-danger" data-action="delete">${t('remove')}</button>
      <span data-confirm hidden><button type="button" class="btn btn-danger" data-action="confirm-delete">${t('confirm')}</button>
      <button type="button" class="btn" data-action="cancel-delete">${t('cancel')}</button></span>` : ''}</div>
    </form>`;
}

export function mountWebhooks(section, client, projectId, locale) {
  const t = key => webhookText(locale, key);
  const base = '/projects/' + projectId + '/webhooks';
  let disposed = false;
  let busy = false;
  let loadVersion = 0;
  const active = () => !disposed && section.isConnected;
  section.innerHTML = `<div class="card"><h3>${t('title')}</h3><p class="text-muted">${t('description')}</p>
    <button type="button" class="btn" data-action="refresh">${t('refresh')}</button></div>
    <div data-secret role="status"></div><div data-webhook-list></div><div data-webhook-new></div>`;
  const fail = err => {
    const code = err?.body?.error;
    if (active()) showToast(t(Object.hasOwn(COPY.en, code) ? code : 'failed'), 'error');
  };
  async function load() {
    const version = ++loadVersion;
    const result = await client.get(base);
    if (!active() || version !== loadVersion) return;
    section.querySelector('[data-webhook-list]').innerHTML = result.webhooks.length
      ? result.webhooks.map(w => renderWebhookForm(w, result.events, locale)).join('') : `<p>${t('empty')}</p>`;
    section.querySelector('[data-webhook-new]').innerHTML = renderWebhookForm(null, result.events, locale);
  }
  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    busy = true;
    const form = event.target;
    const enable = disableForSave([...form.elements]);
    try {
      const body = webhookFormBody(form);
      if (form.dataset.webhookId) await client.patch(base + '/' + form.dataset.webhookId, body);
      else {
        const result = await client.post(base, body);
        if (active()) {
          const box = section.querySelector('[data-secret]');
          box.innerHTML = `<p>${t('secret')}</p><input class="form-input" readonly aria-label="${t('secret')}" autocomplete="off">
            <button type="button" class="btn" data-action="dismiss">${t('dismiss')}</button>`;
          box.querySelector('input').value = result.secret;
        }
      }
      await load();
    } catch (err) { fail(err); }
    finally { busy = false; enable(); }
  };
  const click = async event => {
    const button = event.target.closest('[data-action]');
    if (!button) return;
    const action = button.dataset.action;
    const form = button.closest('form');
    if (action === 'dismiss') { section.querySelector('[data-secret]').replaceChildren(); return; }
    if (action === 'delete' || action === 'cancel-delete') { form.querySelector('[data-confirm]').hidden = action !== 'delete'; return; }
    if (busy) return;
    busy = true;
    const enable = disableForSave(button);
    try {
      if (action === 'test') {
        await client.post(base + '/' + form.dataset.webhookId + '/test', {});
        if (active()) showToast(t('queued'), 'success');
      } else if (action === 'confirm-delete') await client.delete(base + '/' + form.dataset.webhookId);
      await load();
    } catch (err) { fail(err); }
    finally { busy = false; enable(); }
  };
  section.addEventListener('submit', submit);
  section.addEventListener('click', click);
  load().catch(fail);
  return () => {
    disposed = true;
    section.querySelector('[data-secret]')?.replaceChildren();
    section.removeEventListener('submit', submit);
    section.removeEventListener('click', click);
  };
}
